/**
 * Per-clip color-grade presets for the "Фильтры" tool.
 *
 * `filterId` lives on each timeline clip, so neighboring clips may use
 * different looks without leaking the grade across the whole edit:
 *  - `css` drives the live preview (`EditorPreview.tsx` sets it as the
 *    `<video>` element's CSS `filter`) — a cheap, approximate hint, same
 *    spirit as the CSS transition preview vs. the real ffmpeg xfade.
 *  - `ffmpeg` is the authoritative `-vf` fragment baked into the export by
 *    `render-worker.ts`. `null` means "no video filter pass" — the 'none'
 *    preset must leave the render byte-identical to before this feature.
 *
 * CSS and ffmpeg are only APPROXIMATELY matched (different filter math/color
 * spaces), which is expected and fine for a preview.
 */
export interface VideoFilterPreset {
  /** Stable id, persisted on the project. Never rename an existing id. */
  id: string;
  /** Display name (Russian). */
  name: string;
  /** CSS `filter` value for the live preview. Empty string = no CSS filter. */
  css: string;
  /** ffmpeg `-vf` chain fragment for the export, or null for no-op ('none'). */
  ffmpeg: string | null;
}

export const VIDEO_FILTERS: VideoFilterPreset[] = [
  {
    id: 'none',
    name: 'Без фильтра',
    css: '',
    ffmpeg: null,
  },
  {
    id: 'warm',
    name: 'Тёплый',
    css: 'saturate(1.1) sepia(0.12) brightness(1.03)',
    // colorbalance has no 'ym'/'cm' options (only r/g/b × shadow/mid/highlight,
    // see smoke test below) — warmth is red-up/blue-down on midtones instead.
    ffmpeg: 'eq=saturation=1.12:brightness=0.02,colorbalance=rm=0.06:bm=-0.04',
  },
  {
    id: 'cold',
    name: 'Холодный',
    css: 'saturate(1.05) hue-rotate(-8deg) brightness(1.02)',
    ffmpeg: 'eq=saturation=1.05,colorbalance=rm=-0.04:bm=0.08',
  },
  {
    id: 'vivid',
    name: 'Насыщенный',
    css: 'saturate(1.35) contrast(1.1)',
    ffmpeg: 'eq=saturation=1.35:contrast=1.1',
  },
  {
    id: 'bw',
    name: 'Чёрно-белый',
    css: 'grayscale(1) contrast(1.05)',
    ffmpeg: 'hue=s=0,eq=contrast=1.05',
  },
  {
    id: 'vintage',
    name: 'Винтаж',
    css: 'sepia(0.35) contrast(0.95) saturate(0.9)',
    ffmpeg: 'curves=preset=vintage',
  },
  {
    id: 'bright',
    name: 'Ярче',
    css: 'brightness(1.12) contrast(1.03)',
    ffmpeg: 'eq=brightness=0.08:contrast=1.03',
  },
  {
    id: 'fade',
    name: 'Приглушённый',
    css: 'contrast(0.9) brightness(1.05) saturate(0.85)',
    ffmpeg: 'eq=contrast=0.9:brightness=0.04:saturation=0.85',
  },
  {
    id: 'cinematic',
    name: 'Кино',
    css: 'contrast(1.16) saturate(0.88) brightness(0.96)',
    ffmpeg: 'eq=contrast=1.16:saturation=0.88:brightness=-0.025,colorbalance=bs=0.03:rh=0.025',
  },
  {
    id: 'teal-orange',
    name: 'Teal & Orange',
    css: 'contrast(1.12) saturate(1.18) hue-rotate(-5deg)',
    ffmpeg: 'eq=contrast=1.12:saturation=1.18,colorbalance=bs=0.08:gs=0.025:rh=0.08:gh=0.025',
  },
  {
    id: 'soft',
    name: 'Мягкий свет',
    css: 'brightness(1.08) contrast(0.9) saturate(0.95)',
    ffmpeg: 'eq=brightness=0.055:contrast=0.9:saturation=0.95',
  },
  {
    id: 'noir',
    name: 'Нуар',
    css: 'grayscale(1) contrast(1.32) brightness(0.88)',
    ffmpeg: 'hue=s=0,eq=contrast=1.32:brightness=-0.06',
  },
  {
    id: 'sunset',
    name: 'Закат',
    css: 'sepia(0.18) saturate(1.25) hue-rotate(-8deg)',
    ffmpeg: 'eq=saturation=1.25,colorbalance=rm=0.08:gm=0.015:bm=-0.065:rh=0.05',
  },
  {
    id: 'forest',
    name: 'Лесной',
    css: 'saturate(1.1) hue-rotate(12deg) brightness(0.96)',
    ffmpeg: 'eq=saturation=1.1:brightness=-0.02,colorbalance=gm=0.06:rm=-0.025:bm=-0.015',
  },
  {
    id: 'arctic',
    name: 'Арктика',
    css: 'saturate(0.82) hue-rotate(-12deg) brightness(1.08)',
    ffmpeg: 'eq=saturation=0.82:brightness=0.05,colorbalance=rm=-0.055:bm=0.1',
  },
  {
    id: 'rose',
    name: 'Розовый',
    css: 'saturate(1.1) sepia(0.08) hue-rotate(325deg)',
    ffmpeg: 'eq=saturation=1.1,colorbalance=rm=0.07:bm=0.035:gm=-0.025',
  },
  {
    id: 'crisp',
    name: 'Чёткий',
    css: 'contrast(1.2) saturate(1.08) brightness(1.02)',
    ffmpeg: 'eq=contrast=1.2:saturation=1.08:brightness=0.015,unsharp=5:5:0.55:5:5:0.0',
  },
  {
    id: 'matte',
    name: 'Матовый',
    css: 'contrast(0.84) saturate(0.82) brightness(1.06)',
    ffmpeg: 'eq=contrast=0.84:saturation=0.82:brightness=0.045,colorbalance=bs=0.035:rs=0.025',
  },
];

const FILTERS_BY_ID = new Map(VIDEO_FILTERS.map((preset) => [preset.id, preset]));

/** Safe lookup: unknown/missing id falls back to the 'none' preset. */
export function getFilter(id: string | undefined | null): VideoFilterPreset {
  if (id) {
    const found = FILTERS_BY_ID.get(id);
    if (found) return found;
  }
  return FILTERS_BY_ID.get('none')!;
}
